import { FC, useEffect } from 'react';
import { CareMapDrawerPage } from '../../components/CareMapDrawerPage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { ICareMapNoteTarget } from '../model/ICareMapNoteTarget';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { IAppointment } from '../../appointments/IAppointment';
import { NoteDetailBody } from './NoteDetailBody';

interface IViewNoteUsingCareMapDrawerProps {
    noteId: string;
    defaultNoteTarget?: ICareMapNoteTarget;
}

export const ViewNoteUsingCareMapDrawer: FC<IViewNoteUsingCareMapDrawerProps> = ({
    noteId
}) => {
    const history = useInstance(HistoryViewModel);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const model = notesFactory.forActiveCaremap();
    const note = model.getNote(noteId);

    // If the note no longer exists — deleted by us or a collaborator while this drawer page (or the
    // editor we'd return to) was open — don't strand the user on a blank drawer page; pop back to the
    // notes list, as if Back were tapped. This also self-heals the editor's onDeleted history.back(),
    // which lands here on the now-deleted note.
    //
    // Two guards: (1) hasLoaded, so an initial deep-link (notes not fetched yet) doesn't navigate away
    // before the note loads; (2) only pop when THIS view page is the active route — while the editor is
    // stacked on top (route ends in /edit) this page is still mounted and re-renders when the note
    // vanishes, and a back() from here would pop the editor and pre-empt its "Note deleted" alert.
    const hasLoadedNotes = model.hasLoaded;
    const isActiveViewRoute = history.getCurrentLocation().pathname.endsWith(`/notes/${noteId}`);
    useEffect(() => {
        if (hasLoadedNotes && !note && isActiveViewRoute) {
            history.back();
        }
    }, [hasLoadedNotes, note, isActiveViewRoute]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!note) {
        return;
    }

    const editHandler = () => {
        const currentPath = history.getCurrentLocation().pathname;
        history.push(`${currentPath}/edit`);
    };

    const handleAppointmentClick = (appointment: IAppointment) => {
        const basePath = history.getCurrentLocation().pathname.split('/').slice(0, -2).join('/');
        history.push(`${basePath}/appointments/${appointment.appointmentId}`);
    };

    return (
        <CareMapDrawerPage
            title="View Note"
            hasBack={true}
            data-type="view-note-caremap-drawer"
        >
            <NoteDetailBody
                noteId={noteId}
                trackingFrom="caremap-drawer"
                editHandler={editHandler}
                onAppointmentClick={handleAppointmentClick}
                onCloseDrawer={() => history.back()}
            />
        </CareMapDrawerPage>
    );
};
